import type {
  ChoirEvent,
  ChoirFunction,
  DocKind,
  DocumentType,
  EventType,
  Member,
  MemberType,
  PersonalField,
  Priority,
  TransportMode,
  Voice,
} from '../types'

export const voiceLabel: Record<Voice, string> = {
  sopran1: '1. sopran',
  sopran2: '2. sopran',
  alt1: '1. alt',
  alt2: '2. alt',
  tenor1: '1. tenor',
  tenor2: '2. tenor',
  bas1: '1. bas',
  bas2: '2. bas',
  brez: 'Brez glasu',
}

export const voiceShort: Record<Voice, string> = {
  sopran1: 'S1',
  sopran2: 'S2',
  alt1: 'A1',
  alt2: 'A2',
  tenor1: 'T1',
  tenor2: 'T2',
  bas1: 'B1',
  bas2: 'B2',
  brez: '—',
}

export const voiceOrder: Voice[] = [
  'sopran1',
  'sopran2',
  'alt1',
  'alt2',
  'tenor1',
  'tenor2',
  'bas1',
  'bas2',
  'brez',
]

/** Subtle purple family for female voices, green family for male voices. */
export const voiceClass: Record<Voice, string> = {
  sopran1: 'bg-purple-50 text-purple-700 border-purple-200',
  sopran2: 'bg-purple-50/70 text-purple-600 border-purple-100',
  alt1: 'bg-purple-50 text-purple-700 border-purple-200',
  alt2: 'bg-purple-50/70 text-purple-600 border-purple-100',
  tenor1: 'bg-green-50 text-green-700 border-green-200',
  tenor2: 'bg-green-50/70 text-green-600 border-green-100',
  bas1: 'bg-green-50 text-green-700 border-green-200',
  bas2: 'bg-green-50/70 text-green-600 border-green-100',
  brez: 'bg-slate-50 text-ink-muted border-line',
}

export const memberTypeLabel: Record<MemberType, string> = {
  aktivni: 'Aktivni član',
  gostujoci: 'Gostujoči član',
  neaktivni: 'Neaktivni član',
  castni: 'Častni član',
  nekdanji: 'Nekdanji član',
}

export const memberTypeClass: Record<MemberType, string> = {
  aktivni: 'bg-green-50 text-green-700 border-green-200',
  gostujoci: 'bg-purple-50 text-purple-700 border-purple-200',
  neaktivni: 'bg-amber-50 text-amber-700 border-amber-200',
  castni: 'bg-purple-50/70 text-purple-600 border-purple-100',
  nekdanji: 'bg-slate-100 text-ink-muted border-line',
}

export const memberTypeOrder: MemberType[] = [
  'aktivni',
  'gostujoci',
  'neaktivni',
  'castni',
  'nekdanji',
]

export const functionLabel: Record<ChoirFunction, string> = {
  pevec: 'Pevec',
  dirigent: 'Dirigent',
  korepetitor: 'Korepetitor',
  predsednik: 'Predsednik',
  tajnik: 'Tajnik',
  blagajnik: 'Blagajnik',
  reditelj: 'Reditelj',
  umetniska: 'Umetniška komisija',
  disciplinska: 'Disciplinska komisija',
  arhivar: 'Arhivar',
  organizator: 'Organizator gostovanj',
}

export const functionOrder: ChoirFunction[] = [
  'pevec',
  'dirigent',
  'korepetitor',
  'predsednik',
  'tajnik',
  'blagajnik',
  'reditelj',
  'umetniska',
  'disciplinska',
  'arhivar',
  'organizator',
]

export const eventTypeLabel: Record<EventType, string> = {
  vaja: 'Vaja',
  intenzivna: 'Intenzivna vaja',
  sekcijska: 'Sekcijska vaja',
  generalka: 'Generalka',
  nastop: 'Nastop',
  zbor: 'Zbor članov',
  sestanek: 'Sestanek',
}

export const eventTypeShort: Record<EventType, string> = {
  vaja: 'Vaja',
  intenzivna: 'Intenzivna',
  sekcijska: 'Sekcijska',
  generalka: 'Generalka',
  nastop: 'Nastop',
  zbor: 'Zbor članov',
  sestanek: 'Sestanek',
}

export const eventTypeClass: Record<EventType, string> = {
  vaja: 'bg-green-50 text-green-700 border-green-200',
  intenzivna: 'bg-green-500 text-white border-green-500',
  sekcijska: 'bg-green-50/70 text-green-600 border-green-100',
  generalka: 'bg-slate-100 text-ink border-line',
  nastop: 'bg-purple-50 text-purple-700 border-purple-200',
  zbor: 'bg-slate-100 text-ink-muted border-line',
  sestanek: 'bg-slate-50 text-ink-muted border-line',
}

/** Dot color used on calendar cells and timelines. */
export const eventDotClass: Record<EventType, string> = {
  vaja: 'bg-green-500',
  intenzivna: 'bg-green-700',
  sekcijska: 'bg-green-300',
  generalka: 'bg-slate-400',
  nastop: 'bg-purple-500',
  zbor: 'bg-slate-500',
  sestanek: 'bg-slate-300',
}

/** Glasbeni dogodki proti organizacijskim — za elegantne filtre. */
export const glasbeniTipi: EventType[] = ['vaja', 'intenzivna', 'sekcijska', 'generalka', 'nastop']
export const organizacijskiTipi: EventType[] = ['zbor', 'sestanek']

export const priorityLabel: Record<Priority, string> = {
  obicajno: 'Običajno',
  pomembno: 'Pomembno',
  nujno: 'Nujno',
  sprememba: 'Sprememba dogodka',
}

export const priorityClass: Record<Priority, string> = {
  obicajno: 'bg-slate-100 text-ink-muted border-line',
  pomembno: 'bg-purple-50 text-purple-700 border-purple-200',
  nujno: 'bg-rose-50 text-rose-700 border-rose-200',
  sprememba: 'bg-amber-50 text-amber-800 border-amber-200',
}

export function fullName(m: Member): string {
  return `${m.ime} ${m.priimek}`
}

export function initials(m: Member): string {
  return `${m.ime[0] ?? ''}${m.priimek[0] ?? ''}`.toUpperCase()
}

export function eventTitle(e: ChoirEvent): string {
  if (e.naslov) return e.naslov
  if (e.type === 'sekcijska' && e.glasovi?.length) {
    return `Sekcijska vaja — ${e.glasovi.map((v) => voiceLabel[v]).join(', ')}`
  }
  if (e.type === 'sestanek' && e.funkcije?.length) {
    return `Sestanek — ${e.funkcije.map((f) => functionLabel[f]).join(', ')}`
  }
  return eventTypeLabel[e.type]
}

export const docKindLabel: Record<DocKind, string> = {
  vabilo: 'Vabilo',
  dnevni_red: 'Dnevni red',
  gradivo: 'Gradivo',
  zapisnik: 'Zapisnik',
  priloga: 'Priloga',
}

export const docKindClass: Record<DocKind, string> = {
  vabilo: 'bg-green-50 text-green-700 border-green-200',
  dnevni_red: 'bg-slate-100 text-ink-muted border-line',
  gradivo: 'bg-slate-100 text-ink-muted border-line',
  zapisnik: 'bg-purple-50 text-purple-700 border-purple-200',
  priloga: 'bg-slate-50 text-ink-muted border-line',
}

export const personalFieldLabel: Record<PersonalField, string> = {
  naslov: 'Naslov',
  datumRojstva: 'Datum rojstva',
  dokumentTip: 'Vrsta osebnega dokumenta',
  dokumentStevilka: 'Številka osebnega dokumenta',
  prehrana: 'Posebnosti pri prehrani',
}

export const personalFieldOrder: PersonalField[] = [
  'naslov',
  'datumRojstva',
  'dokumentTip',
  'dokumentStevilka',
  'prehrana',
]

export const documentTypeLabel: Record<DocumentType, string> = {
  osebna: 'Osebna izkaznica',
  potni_list: 'Potni list',
}

export const transportModeLabel: Record<TransportMode, string> = {
  vozim: 'Vozim',
  potrebujem: 'Potrebujem prevoz',
  sam: 'Imam svoj prevoz',
}

export function maskiraj(vrednost?: string): string {
  if (!vrednost) return '—'
  const vidno = vrednost.slice(-3)
  return `${'•'.repeat(Math.max(vrednost.length - 3, 3))}${vidno}`
}

const dnevi = ['nedelja', 'ponedeljek', 'torek', 'sreda', 'četrtek', 'petek', 'sobota']
const meseci = [
  'januar',
  'februar',
  'marec',
  'april',
  'maj',
  'junij',
  'julij',
  'avgust',
  'september',
  'oktober',
  'november',
  'december',
]

export function parseDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, (m ?? 1) - 1, d ?? 1)
}

export function formatDate(iso: string, opts: { weekday?: boolean; long?: boolean } = {}): string {
  const d = parseDate(iso)
  const base = opts.long
    ? `${d.getDate()}. ${meseci[d.getMonth()]} ${d.getFullYear()}`
    : `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`
  return opts.weekday ? `${dnevi[d.getDay()]}, ${base}` : base
}

export function formatRange(od: string, doDatum?: string): string {
  if (!doDatum || doDatum === od) return formatDate(od, { long: true })
  const a = parseDate(od)
  const b = parseDate(doDatum)
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `${a.getDate()}.–${b.getDate()}. ${monthName(b.getMonth())} ${b.getFullYear()}`
  }
  return `${formatDate(od, { long: true })} – ${formatDate(doDatum, { long: true })}`
}

export function formatShort(iso: string): string {
  const d = parseDate(iso)
  return `${d.getDate()}. ${d.getMonth() + 1}.`
}

export function weekdayShort(iso: string): string {
  const d = parseDate(iso)
  return ['ned', 'pon', 'tor', 'sre', 'čet', 'pet', 'sob'][d.getDay()]
}

export function monthName(index: number): string {
  return meseci[index]
}

export function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()} ob ${String(d.getHours()).padStart(
    2,
    '0',
  )}.${String(d.getMinutes()).padStart(2, '0')}`
}

export function relativeDays(iso: string, today: string): string {
  const diff = Math.round((parseDate(iso).getTime() - parseDate(today).getTime()) / 86400000)
  if (diff === 0) return 'danes'
  if (diff === 1) return 'jutri'
  if (diff === 2) return 'pojutrišnjem'
  if (diff < 0) return `pred ${Math.abs(diff)} dnevi`
  return `čez ${diff} dni`
}
